import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthCard } from "@/components/AuthCard";
import { PasswordFields } from "@/components/PasswordFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, XCircle, Building2, CalendarDays, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function LeaseSummary({ inv }) {
  if (!inv.property_name) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-5" data-testid="invite-lease-summary">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Building2 className="h-4 w-4 text-primary" /> {inv.property_name}</div>
      {inv.property_address && <div className="text-xs text-slate-500 ml-6">{inv.property_address}</div>}
      {inv.total_rent > 0 && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase text-slate-500">{inv.section8 ? "Your copay" : "Monthly rent"}</div>
            <div className="text-base font-bold text-slate-900">{fmtMoney(inv.section8 ? inv.tenant_portion : inv.total_rent)}</div>
          </div>
          {inv.section8 ? (
            <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase text-amber-700">Section 8 HAP</div>
              <div className="text-base font-bold text-amber-900">{fmtMoney(inv.hap_amount)}</div>
            </div>
          ) : (
            <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 flex items-center gap-2 text-xs text-slate-600"><CalendarDays className="h-4 w-4 text-slate-400" /> Due on the {inv.rent_due_day}{["st","nd","rd"][((inv.rent_due_day + 90) % 100 - 10) % 10 - 1] || "th"}</div>
          )}
        </div>
      )}
    </div>
  );
}

function LandlordInviteSummary({ inv }) {
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 mb-5" data-testid="invite-landlord-summary">
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900"><ShieldCheck className="h-4 w-4" /> Your own VuZuRent landlord account</div>
      <p className="text-xs text-indigo-900/80 mt-1 ml-6">Houses, tenants, Section 8 ledgers, online rent collection, notices, maintenance, listings and more.</p>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="rounded-lg bg-white border border-indigo-100 px-3 py-2"><div className="text-[10px] font-semibold uppercase text-slate-500">Free trial</div><div className="text-base font-bold text-slate-900">{inv.trial_days > 0 ? `${inv.trial_days} days` : "None"}</div></div>
        <div className="rounded-lg bg-white border border-indigo-100 px-3 py-2"><div className="text-[10px] font-semibold uppercase text-slate-500">Then</div><div className="text-base font-bold text-slate-900">{fmtMoney(inv.plan_amount)}<span className="text-xs font-normal text-slate-500">/mo</span></div></div>
      </div>
    </div>
  );
}

export default function AcceptInvite() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [inv, setInv] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setInv({ error: "This invitation link is missing its code. Please use the link from your email." }); return; }
    api.get(`/invites/public/${encodeURIComponent(token)}`)
      .then((r) => { setInv(r.data); setForm({ name: r.data.name || "", phone: r.data.phone || "" }); })
      .catch((e) => setInv({ error: apiErr(e) }));
  }, [token]);

  const isLandlord = inv?.kind === "landlord";
  const submit = async (e) => {
    e.preventDefault();
    if (pw.password.length < 8) return toast.error("Use at least 8 characters");
    if (pw.password !== pw.confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post(`/invites/public/${encodeURIComponent(token)}/accept`, { password: pw.password, name: form.name, phone: form.phone });
      await refresh();
      toast.success(isLandlord ? "Welcome! Your landlord dashboard is ready." : "Welcome! Your tenant portal is ready.");
      navigate(isLandlord ? "/dashboard" : "/portal", { replace: true });
    } catch (err) { toast.error(apiErr(err)); setLoading(false); }
  };

  const googleJoin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/dashboard")}`;
  };

  if (!inv) return <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2"><Loader2 className="h-6 w-6 animate-spin text-primary" /> Opening your invitation…</div>;

  if (inv.error) {
    return (
      <AuthCard title="Invitation not available" subtitle={inv.error} testid="invite-invalid"
        footer={<Link to="/login" className="font-semibold text-primary hover:underline" data-testid="invite-back-login">Already have a login? Sign in</Link>}>
        <div className="rounded-xl bg-rose-50 border border-rose-100 p-5 text-center"><XCircle className="h-10 w-10 text-rose-500 mx-auto" /></div>
      </AuthCard>
    );
  }

  if (inv.account_exists) {
    return (
      <AuthCard title="You already have a login" brand={inv.brand} testid="invite-existing"
        subtitle={<>An account for <b className="text-slate-800">{inv.email}</b> already exists. Sign in and your lease from {inv.landlord_name} will be linked automatically.</>}>
        <Button asChild className="w-full h-11 gap-2" data-testid="invite-go-login"><Link to="/login">Sign in <ArrowRight className="h-4 w-4" /></Link></Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={isLandlord ? "You're invited to VuZuRent" : `${inv.landlord_name} invited you`} brand={inv.brand} testid="invite-card"
      subtitle={isLandlord
        ? <>Set up your VuZuRent <b>landlord</b> login for <b className="text-slate-800" data-testid="invite-email">{inv.email}</b> and start managing your own rentals.</>
        : <>Set up your VuZuRent tenant login for <b className="text-slate-800" data-testid="invite-email">{inv.email}</b> to see your balance, pay rent, download receipts and send maintenance requests.</>}
      footer={<>Already have a login? <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link></>}>
      {isLandlord ? <LandlordInviteSummary inv={inv} /> : <LeaseSummary inv={inv} />}
      <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label htmlFor="inv-name">Your name</Label><Input id="inv-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-11" data-testid="invite-name" /></div>
          <div className="space-y-1.5"><Label htmlFor="inv-phone">Phone (optional)</Label><Input id="inv-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="(555) 010-2020" className="h-11" data-testid="invite-phone" /></div>
        </div>
        <PasswordFields value={pw} onChange={setPw} prefix="invite" />
        <Button type="submit" disabled={loading} className="w-full h-11 text-sm font-semibold gap-2" data-testid="invite-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create my login <ArrowRight className="h-4 w-4" /></>}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-4"><div className="h-px flex-1 bg-slate-200" /><span className="text-xs font-semibold uppercase tracking-wider text-slate-400">or</span><div className="h-px flex-1 bg-slate-200" /></div>
      <Button type="button" variant="outline" onClick={googleJoin} className="w-full h-11 text-sm font-semibold gap-2.5 bg-white hover:bg-slate-50" data-testid="invite-google">
        <svg className="h-[18px] w-[18px]" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
        Continue with Google ({inv.email})
      </Button>
      <p className="text-[11px] text-slate-400 mt-2 text-center">Use the Google account for {inv.email} — {isLandlord ? "your landlord account is created automatically" : "your lease links automatically"}.</p>
    </AuthCard>
  );
}
